/**
 * About Page for trAInedup.ai
 * Tells the story and mission of the platform
 */

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Target, Users, Lightbulb, Heart, ArrowRight } from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { ROUTES } from '../utils/constants';
import { SEOHead } from '../components/seo/SEOHead';

/**
 * About page component showcasing mission, values, and team
 * Implements trAInedup.ai brand voice and storytelling 
 */
export const AboutPage: React.FC = () => {
  const { t } = useTranslation('pages/about');
  const videoRef = useRef<HTMLVideoElement>(null);



  return (
    <Layout>
      <SEOHead
        title="About Us - trAInedup.ai"
        description="Learn about trAInedup.ai's mission to make AI education accessible to everyone. Discover our story, values, and commitment to practical, supportive AI learning."
        canonical="https://trainedup.ai/about"
        keywords={['about trAInedup', 'AI education mission', 'learn about AI training', 'AI learning platform']}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-800 mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-neutral-600 mb-8">
              {t('hero.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-montserrat font-bold text-3xl text-neutral-800 mb-6">
                  {t('story.title')}
                </h2>
                <p className="text-neutral-600 mb-6">{t('story.p1')}</p>
                <p className="text-neutral-600 mb-6">{t('story.p2')}</p>
                <p className="text-neutral-600">{t('story.p3')}</p>
              </div>
              <div className="bg-gradient-to-br from-primary-100 to-secondary-100 rounded-lg p-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                    <Lightbulb className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-montserrat font-semibold text-xl text-neutral-800 mb-4">
                    {t('vision.title')}
                  </h3>
                  <p className="text-neutral-600">{t('vision.desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-neutral-100">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-montserrat font-bold text-3xl text-neutral-800 mb-6">
                {t('values.title')}
              </h2>
              <p className="text-xl text-neutral-600">{t('values.desc')}</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Value 1: Approachable */}
              <Card className="text-center" hover>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-4">{t('values.approachable.title')}</h3>
                <p className="text-neutral-600">{t('values.approachable.desc')}</p>
              </Card>

              {/* Value 2: Supportive */}
              <Card className="text-center" hover>
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-4">{t('values.supportive.title')}</h3>
                <p className="text-neutral-600">{t('values.supportive.desc')}</p>
              </Card>

              {/* Value 3: Empowering */}
              <Card className="text-center" hover>
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-4">{t('values.empowering.title')}</h3>
                <p className="text-neutral-600">{t('values.empowering.desc')}</p>
              </Card>

              {/* Value 4: Practical */}
              <Card className="text-center" hover>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lightbulb className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-4">{t('values.practical.title')}</h3>
                <p className="text-neutral-600">{t('values.practical.desc')}</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-montserrat font-bold text-3xl text-neutral-800 mb-6">
                {t('approach.title')}
              </h2>
              <p className="text-xl text-neutral-600">{t('approach.desc')}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Approach 1 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="font-montserrat font-semibold text-xl mb-4">{t('approach.personalized.title')}</h3>
                <p className="text-neutral-600">{t('approach.personalized.desc')}</p>
              </div>

              {/* Approach 2 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="font-montserrat font-semibold text-xl mb-4">{t('approach.bitesized.title')}</h3>
                <p className="text-neutral-600">{t('approach.bitesized.desc')}</p>
              </div>

              {/* Approach 3 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="font-montserrat font-semibold text-xl mb-4">{t('approach.community.title')}</h3>
                <p className="text-neutral-600">{t('approach.community.desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Climbing AI Mountain Video Section */}
      <section className="py-20 bg-neutral-100">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg">
              <video 
                ref={videoRef}
                className="w-full h-full object-cover"
                controls
                loop
                playsInline
                preload="metadata"
              >
                <source 
                  src="https://firebasestorage.googleapis.com/v0/b/trainedup-a4915.firebasestorage.app/o/Promo%2FPromo%20video%20home%20page.mp4?alt=media&token=f0e9e512-5797-45e8-941f-43db962a229a" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-montserrat font-bold text-3xl text-neutral-800 mb-6">
                {t('different.title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <h3 className="font-montserrat font-semibold text-xl mb-4">
                  {t('different.generic.title')}
                </h3>
                <p className="text-neutral-600 mb-4">{t('different.generic.desc1')}</p>
                <p className="text-neutral-600">{t('different.generic.desc2')}</p>
              </Card>

              <Card>
                <h3 className="font-montserrat font-semibold text-xl mb-4">
                  {t('different.experts.title')}
                </h3>
                <p className="text-neutral-600 mb-4">{t('different.experts.desc1')}</p>
                <p className="text-neutral-600">{t('different.experts.desc2')}</p>
              </Card>

              <Card>
                <h3 className="font-montserrat font-semibold text-xl mb-4">
                  {t('different.application.title')}
                </h3>
                <p className="text-neutral-600 mb-4">{t('different.application.desc1')}</p>
                <p className="text-neutral-600">{t('different.application.desc2')}</p>
              </Card>

              <Card>
                <h3 className="font-montserrat font-semibold text-xl mb-4">
                  {t('different.community.title')}
                </h3>
                <p className="text-neutral-600 mb-4">{t('different.community.desc1')}</p>
                <p className="text-neutral-600">{t('different.community.desc2')}</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-montserrat font-bold text-3xl text-neutral-800 mb-6">
              {t('team.title')}
            </h2>
            <p className="text-xl text-neutral-600 mb-12">{t('team.desc')}</p>
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg p-8">
              <p className="text-lg text-neutral-700 mb-6">{t('team.p1')}</p>
              <p className="text-neutral-600">{t('team.p2')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl mb-6">{t('cta.title')}</h2>
            <p className="text-xl mb-8 opacity-90">{t('cta.desc')}</p>
            <Link to={ROUTES.register}>
              <Button variant="primary" size="lg" icon={ArrowRight} iconPosition="right">
                {t('cta.button')}
              </Button>
            </Link>
            <p className="mt-6 text-sm opacity-75">{t('cta.footer')}</p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

